/* Core & libraries */
import React, {useEffect, useState, useMemo} from "react";
import { IntlProvider } from "react-intl";
import { ThemeProvider } from "react-jss";
import { Helmet, HelmetProvider } from "react-helmet-async";

/* Helpers, constants, custom methods, internal libraries */
import messages_pt_BR from "../../resources/translations/pt_BR.json";
import messages_pt_PT from "../../resources/translations/pt_PT.json";
import messages_de_DE from "../../resources/translations/de_DE.json";
import messages_ar_SA from "../../resources/translations/ar_SA.json";
import messages_cs_CZ from "../../resources/translations/cs_CZ.json";
import messages_en_UK from "../../resources/translations/en_UK.json";
import messages_en_US from "../../resources/translations/en_US.json";
import messages_es_ES from "../../resources/translations/es_ES.json";
import messages_fr_FR from "../../resources/translations/fr_FR.json";
import messages_fr_CA from "../../resources/translations/fr_CA.json";
import messages_it_IT from "../../resources/translations/it_IT.json";
import messages_ja_JP from "../../resources/translations/ja_JP.json";
import messages_ko_KR from "../../resources/translations/ko_KR.json";
import messages_nb_NO from "../../resources/translations/nb_NO.json";
import messages_pl_PL from "../../resources/translations/pl_PL.json";
import messages_sv_SE from "../../resources/translations/sv_SE.json";
import messages_zh_CN from "../../resources/translations/zh_CN.json";
import { useLocaleContext } from "../../contexts/localeContext";
import { useUserService } from "../../services/userService";
import { ACCEPTED_LANGUAGES, AIRLINES, securityPolicyContent } from '../../constants/appConstants';

/* Components */
import getTheme from "../../resources/themes/themes";

/* Styles */

// used in app and unit-tests
export const messages = {
  de_DE: messages_de_DE,
  pt_BR: messages_pt_BR,
  pt_PT: messages_pt_PT,
  ar_SA: messages_ar_SA,
  cs_CZ: messages_cs_CZ,
  en_AU: messages_en_UK,
  en_CA: messages_en_US,
  en_DE: messages_en_US,
  en_ES: messages_en_US,
  en_FR: messages_en_US,
  en_IE: messages_en_UK,
  en_IN: messages_en_US,
  en_IT: messages_en_US,
  en_MX: messages_en_US,
  en_NZ: messages_en_UK,
  en_UK: messages_en_UK,
  en_US: messages_en_US,
  en_XL: messages_en_US,
  es_ES: messages_es_ES,
  es_MX: messages_es_ES,
  es_US: messages_es_ES,
  es_XL: messages_es_ES,
  es_CO: messages_es_ES,
  es_CL: messages_es_ES,
  es_PE: messages_es_ES,
  es_AR: messages_es_ES,
  es_CA: messages_es_ES,
  fr_CA: messages_fr_CA,
  fr_FR: messages_fr_FR,
  fr_US: messages_en_US,
  it_IT: messages_it_IT,
  ja_JP: messages_ja_JP,
  ko_KR: messages_ko_KR,
  nb_NO: messages_nb_NO,
  pl_PL: messages_pl_PL,
  pt_XL: messages_pt_BR,
  pt_US: messages_en_US,
  sv_SE: messages_sv_SE,
  zh_CN: messages_zh_CN,
};

const MainWrapper = ({children}) => {
  const [theme, setTheme] = useState(null);

  const { userDetails } = useUserService();

  const locale = useLocaleContext();
  const providerCode = userDetails?.userInfo?.providerInfo?.providerCode;

  useEffect(() => {
    if (providerCode) {
      setTheme(providerCode);
    }
  }, [providerCode]);

  const themeProperties = useMemo(() => theme ? getTheme(theme) : null, [theme]);

  const getMetaTags = airline => {
    switch (airline) {
      case AIRLINES.AA:
        return (
          <>
            <meta
              name="description"
              content="AAdvantage Business℠ membership includes access to a suite of tools to streamline travel management for your company. Travelers can also update their program preferences."
            />
            <meta property="og:image" content="%PUBLIC_URL%/imgs/aa_meta_image.jpg" />
          </>
        );

      case AIRLINES.AS:
        return (
          <>
            <meta name="description" content="Alaska Airlines" />
          </>
        );

      case AIRLINES.CHOICE:
        return (
          <>
            <meta name="description" content="Choice Hotels" />
          </>
        );

      default:
        return (
          <>
            <meta name="description" content="Universal Connect" />
          </>
        );
    }
  }

  return (
    theme &&
    <HelmetProvider>
      <Helmet>
        { getMetaTags(theme) }

        {
          process.env.REACT_APP_CSP === "true" && (
            <meta
              http-equiv="Content-Security-Policy"
              content={securityPolicyContent[providerCode]}
            />
          )
        }
      </Helmet>
      <ThemeProvider theme={themeProperties}>
        <IntlProvider locale={ACCEPTED_LANGUAGES[locale]} messages={messages[locale]}>
          { children }
        </IntlProvider>
      </ThemeProvider>
    </HelmetProvider>
  )
};

export default MainWrapper;
